/** Returns the element currently in fullscreen, or null. */
export function getCurrentFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

/**
 * True when the standard Fullscreen API reports a fullscreen element, or when
 * legacy WebKit reports full-screen (including some iOS video full-screen paths).
 */
export function isBrowserFullscreen() {
  if (getCurrentFullscreenElement()) return true;
  try {
    if (typeof document.webkitIsFullScreen === 'boolean' && document.webkitIsFullScreen) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** Diagonal expand arrows (four corners) for enter-fullscreen. */
export function getBrowserFullscreenEnterIconSvg() {
  return (
    '<svg class="fullscreen-remote-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ' +
    'd="M9 4H4v5M15 4h5v5M4 15v5h5M20 15v5h-5"/>' +
    '</svg>'
  );
}

/** Diagonal contract arrows for exit-fullscreen. */
export function getBrowserFullscreenExitIconSvg() {
  return (
    '<svg class="fullscreen-remote-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ' +
    'd="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>' +
    '</svg>'
  );
}

function getRequestFullscreenMethod(element) {
  if (!element) return null;
  return (
    element.requestFullscreen ||
    element.webkitRequestFullscreen ||
    element.webkitRequestFullScreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullscreen ||
    null
  );
}

/**
 * Elements to try for page fullscreen (mobile Safari often rejects documentElement;
 * body or a visible root container sometimes succeeds on Android / iPad).
 */
function getPageFullscreenElementCandidates() {
  const list = [document.documentElement, document.body];
  const tvWrapper = document.getElementById('tvWrapper');
  if (tvWrapper) list.push(tvWrapper);
  const remoteMobileRoot = document.querySelector('.remote-mobile');
  if (remoteMobileRoot) list.push(remoteMobileRoot);
  return list.filter((element, index, array) => element && array.indexOf(element) === index);
}

function tryInvokeRequestFullscreen(element) {
  const request = getRequestFullscreenMethod(element);
  if (typeof request !== 'function') return Promise.resolve(false);
  const options = { navigationUI: 'hide' };
  try {
    const first = request.call(element, options);
    if (first && typeof first.then === 'function') {
      return first.then(() => true).catch(() => {
        try {
          const second = request.call(element);
          if (second && typeof second.then === 'function') {
            return second.then(() => true).catch(() => false);
          }
          return Boolean(second);
        } catch {
          return false;
        }
      });
    }
    return Promise.resolve(Boolean(first));
  } catch {
    try {
      const fallback = request.call(element);
      if (fallback && typeof fallback.then === 'function') {
        return fallback.then(() => true).catch(() => false);
      }
      return Promise.resolve(Boolean(fallback));
    } catch {
      return Promise.resolve(false);
    }
  }
}

/**
 * iPhone Safari often has no working Fullscreen API on arbitrary elements; native
 * video full-screen still hides the browser chrome for the stream.
 */
function tryEnterIosVideoElementFullscreen() {
  const activeVideo =
    document.querySelector('video.channel-video.active') || document.querySelector('video.channel-video');
  if (!activeVideo || typeof activeVideo.webkitEnterFullscreen !== 'function') {
    return false;
  }
  try {
    activeVideo.webkitEnterFullscreen();
    return true;
  } catch {
    return false;
  }
}

/**
 * Enter or exit browser fullscreen on the current document (hides browser chrome where supported).
 * Must be called from a user gesture (e.g. button click). Avoid `await` before requests so the
 * user-activation chain is preserved on strict mobile browsers.
 */
export function toggleBrowserFullscreen() {
  if (isBrowserFullscreen()) {
    try {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        void document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        void document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        void document.msExitFullscreen();
      }
    } catch (error) {
      try {
        console.debug('exitFullscreen failed', error);
      } catch {
        /* ignore */
      }
    }
    return;
  }

  const candidates = getPageFullscreenElementCandidates();
  let chain = Promise.resolve(false);
  for (const element of candidates) {
    chain = chain.then(previousSuccess => {
      if (previousSuccess) return true;
      return tryInvokeRequestFullscreen(element);
    });
  }
  void chain.then(pageEntered => {
    if (pageEntered) return;
    if (tryEnterIosVideoElementFullscreen()) return;
    try {
      console.debug('toggleBrowserFullscreen: no fullscreen path succeeded on this device');
    } catch {
      /* ignore */
    }
  });
}

/**
 * Registers listener for fullscreen changes across vendor-prefixed events and
 * iOS native video full-screen (so toolbar state and icons stay in sync).
 * @returns {function} Call to remove listeners.
 */
export function onBrowserFullscreenChange(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }

  const wrapped = () => listener();
  const documentEventTypes = [
    'fullscreenchange',
    'webkitfullscreenchange',
    'mozfullscreenchange',
    'MSFullscreenChange'
  ];
  documentEventTypes.forEach(eventType => {
    document.addEventListener(eventType, wrapped);
  });

  const videoFullscreenBegin = () => wrapped();
  const videoFullscreenEnd = () => wrapped();
  document.addEventListener('webkitbeginfullscreen', videoFullscreenBegin, true);
  document.addEventListener('webkitendfullscreen', videoFullscreenEnd, true);

  return () => {
    documentEventTypes.forEach(eventType => {
      document.removeEventListener(eventType, wrapped);
    });
    document.removeEventListener('webkitbeginfullscreen', videoFullscreenBegin, true);
    document.removeEventListener('webkitendfullscreen', videoFullscreenEnd, true);
  };
}
